import { Collection } from '@msw/data'
import { categorySchema } from 'app/src/msw/companyagreement/schemas/categoriesSchema'
import type { Category } from 'app/src/services/welfareportal/companyagreement-api'

/**
 * @deprecated This collection is now initialized dynamically based on the JSON files in the `data/catalog/categories` directory. The `initCategoriesCollections` function should be called during the setup phase to populate this collection with the appropriate category data.
 * A record that maps category keys to collections of categories, where each collection is structured according to the `categorySchema`. This collection is used to store categories based on their types and relationships, but it is now recommended to load all categories from a single JSON file using the `getAllStaticCategories` function for easier management and access.
 */
export const categoriesCollection: Record<string, Collection<typeof categorySchema>> = {}

/**
 * Collection that stores all categories according to the `categorySchema`
 */
export const categoryCollection = new Collection({ schema: categorySchema })

/**
 * @deprecated This function is now used to initialize the `categoriesCollection` by loading category data from JSON files corresponding to each category type. It is recommended to use the `getAllStaticCategories` function instead, which loads all categories from a single JSON file for easier management and access.
 * Initializes the `categoriesCollection` by fetching category data from a JSON file and creating a new collection for each category type. Each collection is structured according to the `categorySchema`.
 */
export const initCategoriesCollections = async () => {
  try {
    const jsonData = await (await fetch(`/data/catalog/categories/index.json`)).json()

    jsonData.items.forEach(async (item: string) => {
      categoriesCollection[item] = new Collection({ schema: categorySchema })
    })

    console.log('Initialized categoriesCollection with keys:', Object.keys(categoriesCollection))
  } catch (error) {
    console.error('Error loading categories:', error)
  }
}

/**
 * @deprecated This function is now used to create seeds for the `categoriesCollection` by fetching category data from JSON files corresponding to each category type. It is recommended to use the `getAllStaticCategories` function instead, which loads all categories from a single JSON file for easier management and access.
 * Creates seeds for the `categoriesCollection` by fetching category data from JSON files corresponding to each category type. If a collection already contains data, it will skip seeding for that collection. Each category is created in the respective collection based on its type and relationship.
 */
export const createCategoriesCollectionsSeeds = async () => {
  Object.values(categoriesCollection).forEach(async (collection, index) => {
    const existingData = await collection.findMany()

    if (existingData.length > 0) {
      return
    }

    try {
      const jsonData = await (
        await fetch(`/data/catalog/categories/${Object.keys(categoriesCollection)[index]}/categories.json`)
      ).json()

      jsonData.items.forEach(async (item: Category) => {
        const createdCategory = await collection.create(item)
        console.log('createdCategories', createdCategory)
      })
    } catch (error) {
      console.error('Error loading categories:', error)
    }
  })
}

/**
 * @deprecated This function is now used to fetch all categories from the `categoriesCollection` by iterating through each collection and concatenating the results into a single array. It is recommended to use the `getAllStaticCategories` function instead, which loads all categories from a single JSON file for easier management and access.
 * Fetches all categories from the `categoriesCollection` by iterating through each collection and concatenating the results into a single array. This function is useful for retrieving all categories regardless of their type or relationship, but it is now recommended to load all categories from a single JSON file using the `getAllStaticCategories` function for easier management and access.
 */
export const getAllCategories = async (): Promise<Category[]> => {
  let allCategories: Category[] = []
  await Promise.all(
    Object.values(categoriesCollection).map(async (collection) => {
      const categories = await collection.findMany()
      allCategories = allCategories.concat(categories)
    }),
  )
  console.log('Current allCategories:', allCategories)
  return allCategories
}

/**
 * @deprecated This function is now used to fetch all categories from JSON files corresponding to each category type and concatenate them into a single array. It is recommended to use the `getAllStaticCategories` function instead, which loads all categories from a single JSON file for easier management and access.
 * Function to fetch all categories from JSON files corresponding to each category type and concatenate them into a single array. This function iterates through the index of category files, loads the categories from each file, and combines them into a single array that is returned as a promise. If there is an error during the fetch operation, it logs the error and returns an empty array.
 * @returns A promise that resolves to an array of all categories.
 */
export const getAllStaticCategories = async (): Promise<Category[]> => {
  try {
    const indexData = await (await fetch('/data/catalog/categories/index.json')).json()

    const categoriesByType = await Promise.all(
      indexData.items.map(async (categoryKey: string) => {
        const jsonData = await (await fetch(`/data/catalog/categories/${categoryKey}/categories.json`)).json()

        return jsonData.items as Category[]
      }),
    )

    const allCategories = categoriesByType.flat()

    console.log('Loaded categories:', allCategories)
    return allCategories
  } catch (error) {
    console.error('Error loading categories:', error)
    return []
  }
}

/**
 * @deprecated This function is now used to fetch categories for a specific category key from a JSON file and return them as an array of `Category` objects. It is recommended to use the `getAllStaticCategories` function instead, which loads all categories from a single JSON file for easier management and access.
 * Function to fetch categories for a specific category key from a JSON file and return them as an array of `Category` objects. This function is useful for retrieving categories based on their type or relationship, but it is now recommended to load all categories from a single JSON file using the `getAllStaticCategories` function for easier management and access. If there is an error during the fetch operation, it logs the error and returns an empty array.
 * @param key The category key for which to fetch categories.
 * @returns A promise that resolves to an array of `Category` objects corresponding to the specified key.
 */
export const getKeyStaticCategories = async (key: string): Promise<Category[]> => {
  try {
    const jsonData = await (await fetch(`/data/catalog/categories/${key}/categories.json`)).json()
    return (jsonData?.items || []) as Category[]
  } catch (error) {
    console.error('Error loading categories:', error)
    return []
  }
}

/**
 * Function to create seeds for the `categoryCollection` by fetching category data from JSON files corresponding to each category type. This function iterates through the index of category files, loads the categories from each file, and creates entries in the `categoryCollection` based on the category data.
 */
export const createCategoryCollectionSeeds = async () => {
  try {
    const categories = await fetch('/data/catalog/categories.json').then((r) => r.json())

    for (const category of categories?.items || []) {
      await categoryCollection.create(category)
    }
  } catch (error) {
    console.error('Error loading categories:', error)
  }
}

/**
 * Function to create seeds for the `categoryCollection` by fetching category data from JSON files corresponding to each category type. This function iterates through the index of category files, loads the categories from each file, and creates entries in the `categoryCollection` based on the category data.
 */
export const getAllStaticCategoriesCollection = async () => {
  try {
    const categories = await fetch('/data/catalog/categories.json').then((r) => r.json())
    return categories?.items as (Category & { serviceCategory: string })[]
  } catch (error) {
    console.error('Error loading categories:', error)
    return []
  }
}
